import Box from '@mui/material/Box';
import { Subscription } from '@rails/actioncable';
import { Job_Read, JobInvoicingUpdateChannel_Read } from '@treadinc/horizon-api-spec';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Job, JobInvoicing } from '~hooks/useJob/models';
import JobCardPdfTypeDialog from '~hooks/useJobCardPdf/JobCardPdfTypeDialog';
import { JobCardPdfRecipientType } from '~hooks/useJobCardPdf/models';
import { useJobCardPdf } from '~hooks/useJobCardPdf/useJobCardPdf';
import { Order } from '~hooks/useOrders/models';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ActionsColumn from './components/JobsTable/Columns/ActionsColumn';
import DriverAndTruckColumn from './components/JobsTable/Columns/DriverAndTruckColumn';
import InvoiceColumn from './components/JobsTable/Columns/InvoiceColumn';
import LoadsColumn from './components/JobsTable/Columns/LoadsColumn';
import MarginColumn from './components/JobsTable/Columns/MarginColumn';
import RequestedEquipmentTypeColumn from './components/JobsTable/Columns/RequestedEquipmentTypeColum';
import StatusColumn from './components/JobsTable/Columns/StatusColumn';
import TimeAndDurationColumn from './components/JobsTable/Columns/TimeAndDurationColumn';
import TotalQuantityColumn from './components/JobsTable/Columns/TotalQuantityColumn';
import JobsTableExpandedBlock from './components/JobsTable/ExpandedBlock/ExpandedBlock';
import { columns } from './components/JobsTable/jobs-table-columns';
import TableHeader from './components/JobsTable/TableHeader';
import { JobsQueryContextProvider } from './components/OrdersList/ExpandedBlock/JobsQueryContext';

interface FilesSingleJobProps {
  job: JobInvoicing;
  order: Order;
}

const singleJobColumns = columns.filter((column) => column.key !== 'checkbox');
const singleJobGridTemplateColumns = singleJobColumns
  .map((column) => column.size)
  .join(' ');
const rowGridColumn = `span ${singleJobColumns.length}`;

export default function FilesSingleJob({ job: jobProp, order }: FilesSingleJobProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { generateJobCardPdf, generatingJobIds } = useJobCardPdf();
  const [pdfDialogJob, setPdfDialogJob] = useState<Job | null>(null);

  const handleViewJobCardPdf = useCallback((job: Job) => {
    setPdfDialogJob(job);
    return Promise.resolve();
  }, []);

  const handlePdfTypeSelect = useCallback(
    (recipientType: JobCardPdfRecipientType) => {
      if (pdfDialogJob) {
        generateJobCardPdf(pdfDialogJob, recipientType);
      }
      setPdfDialogJob(null);
    },
    [generateJobCardPdf, pdfDialogJob],
  );

  const [job, setJob] = useState(jobProp);

  const jobsRTUSubscription = useRef<Subscription | null>(null);
  const jobsInvoicingRTUSubscription = useRef<Subscription | null>(null);

  const handleJobUpdated = useCallback(
    (job: Job) => {
      const jobInvoicing = JobInvoicing.fromJobOrJobInvoicingAndInvoices(
        job,
        jobProp.receivableInvoice,
        jobProp.payableInvoice,
      );

      setJob(jobInvoicing);
    },
    [jobProp.receivableInvoice, jobProp.payableInvoice],
  );

  const subscribeToJobsRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.CompanyJobUpdateChannel,
      companyId,
      (sub) => (jobsRTUSubscription.current = sub),
      ({ data }: { data: Job_Read }) => {
        if (data.id === jobProp.id) {
          const parsedJob = JobInvoicing.fromJobOrJobInvoicingAndInvoices(
            Job.parse(data),
            job.receivableInvoice,
            job.payableInvoice,
          );

          setJob(parsedJob);
        }
      },
    );
  }, [companyId, jobProp.id, job.receivableInvoice, job.payableInvoice]);

  const subscribeToJobsInvoicingRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.JobInvoicingUpdateChannel,
      companyId,
      (sub) => (jobsInvoicingRTUSubscription.current = sub),
      (payload: JobInvoicingUpdateChannel_Read) => {
        if (payload.data.id === jobProp.id) {
          const parsedJob = JobInvoicing.parse(payload.data);

          setJob(parsedJob);
        }
      },
    );
  }, [companyId, jobProp.id]);

  useEffect(() => {
    if (!jobsRTUSubscription.current) {
      subscribeToJobsRTU();
    }

    return () => {
      jobsRTUSubscription.current?.unsubscribe();
      jobsRTUSubscription.current = null;
    };
  }, [subscribeToJobsRTU]);

  useEffect(() => {
    if (!jobsInvoicingRTUSubscription.current) {
      subscribeToJobsInvoicingRTU();
    }

    return () => {
      jobsInvoicingRTUSubscription.current?.unsubscribe();
      jobsInvoicingRTUSubscription.current = null;
    };
  }, [subscribeToJobsInvoicingRTU]);

  useEffect(() => {
    setJob(jobProp);
  }, [jobProp]);

  return (
    <JobsQueryContextProvider order={order} companyId={companyId}>
      <Box
        sx={(theme) => ({
          bgcolor: 'background.paper',
          borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          borderLeft: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          borderRight: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          display: 'grid',
          gridAutoRows: '70px',
          gridColumn: rowGridColumn,
          gridTemplateColumns: singleJobGridTemplateColumns,
          gridTemplateRows: '35px',
          overflow: 'hidden',
        })}
      >
        {singleJobColumns.map((column) => (
          <TableHeader key={column.key} title={column.title} sx={column.sx} />
        ))}

        <DriverAndTruckColumn sx={{ ...singleJobColumns[0].sx }} job={job} />
        <RequestedEquipmentTypeColumn sx={{ ...singleJobColumns[1].sx }} job={job} />
        <StatusColumn
          job={job}
          onJobUpdated={setJob}
          sx={{ ...singleJobColumns[2].sx }}
        />
        <TimeAndDurationColumn sx={{ ...singleJobColumns[3].sx }} job={job} />
        <LoadsColumn sx={{ ...singleJobColumns[4].sx }} job={job} />
        <TotalQuantityColumn sx={{ ...singleJobColumns[5].sx }} job={job} />
        <InvoiceColumn
          invoiceType="receivable"
          job={job}
          onJobUpdated={setJob}
          sx={{ ...singleJobColumns[6].sx }}
        />
        <InvoiceColumn
          invoiceType="payable"
          job={job}
          onJobUpdated={setJob}
          sx={{ ...singleJobColumns[7].sx }}
        />
        <MarginColumn sx={{ ...singleJobColumns[8].sx }} job={job} />
        <ActionsColumn
          isGeneratingJobCardPdf={generatingJobIds.has(job.id)}
          job={job}
          onJobUpdated={setJob}
          onViewJobCardPdf={handleViewJobCardPdf}
          sx={{ ...singleJobColumns[9].sx }}
        />
      </Box>

      <JobsTableExpandedBlock
        defaultSelectedTab="summary"
        job={job}
        onJobUpdated={handleJobUpdated}
        tabsSx={{ px: 0 }}
      />
      <JobCardPdfTypeDialog
        isOpen={pdfDialogJob !== null}
        onClose={() => setPdfDialogJob(null)}
        onSelect={handlePdfTypeSelect}
      />
    </JobsQueryContextProvider>
  );
}
